import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';

const WARNING_MS = 25 * 60 * 1000;
const EXPIRY_MS = 30 * 60 * 1000;
const COUNTDOWN_MS = 5 * 60 * 1000;
const STORAGE_KEY = 'codekeeper:lastActivity';

type TimerState = 'idle' | 'warning' | 'expired';

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function useInactivityTimer() {
  const [state, setState] = useState<TimerState>('idle');
  const [countdown, setCountdown] = useState(() => formatCountdown(COUNTDOWN_MS));
  const location = useLocation();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const warningStartRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(expiryTimerRef.current);
    clearInterval(countdownIntervalRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearAllTimers();
    setState('idle');
    setCountdown(formatCountdown(COUNTDOWN_MS));
    localStorage.setItem(STORAGE_KEY, String(Date.now()));

    warningTimerRef.current = setTimeout(() => {
      setState('warning');
      warningStartRef.current = Date.now();

      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - warningStartRef.current;
        setCountdown(formatCountdown(COUNTDOWN_MS - elapsed));
      }, 1000);
    }, WARNING_MS);

    expiryTimerRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      setState('expired');
    }, EXPIRY_MS);
  }, [clearAllTimers]);

  useEffect(() => {
    resetTimer();
    return clearAllTimers;
  }, []);

  useEffect(() => {
    if (state === 'warning') return;

    window.addEventListener('click', resetTimer);
    window.addEventListener('keydown', resetTimer);
    return () => {
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [resetTimer, state]);

  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      resetTimer();
    }
  }, [location.pathname, resetTimer]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) resetTimer();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [resetTimer]);

  return { state, countdown, resetTimer };
}

export { useInactivityTimer };
